/*  src/App.jsx  */
import React, { useContext } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from './context/AuthContext';

/* Auth / common pages */
import Login            from './pages/Login';
import Signup           from './pages/Signup';
import ForgotPassword   from './pages/ForgotPassword';
import Register         from './pages/Register';
import UsersTable       from './pages/UsersTable';

/* Dashboards */
import StudentDashboard    from './pages/StudentDashboard';
import WardenDashboard     from './pages/WardenDashboard';
import GatekeeperDashboard from './pages/GatekeeperDashboard';
import AdminDashboard      from './pages/AdminDashboard';
import AnalyticsDashboard  from './pages/AnalyticsDashboard';
import ChangePasswordFirst from './pages/ChangePasswordFirst';

/* Listings / tables */
import StudentsTable from './pages/StudentsTable';
import GuestsTable   from './pages/GuestsTable';

/* Gate-keeper guest entry */
import GuestEntry from './pages/GuestEntry';

/* VC-office */
import VcOfficeGuests from './pages/VcOfficeGuests';

/* Warden assignment */
import WardenAssignment from './pages/WardenAssignment';

/* Hostels Management */
import HostelsManagement from './pages/HostelsManagement';

/* System Settings */
import SystemSettings from './pages/SystemSettings';

import './styles/App.css';

export default function App () {
  const { user, logout } = useContext(AuthContext);
  const navigate         = useNavigate();
  const { pathname }     = useLocation();
  const year             = new Date().getFullYear();

  const handleLogout = () => { logout(); navigate('/login'); };

  const defaultPath = () => {
    if (!user) return '/login';
    switch (user.role) {
      case 'student'     : return '/student';
      case 'warden'      : return '/warden';
      case 'gatekeeper'  : return '/gatekeeper';
      case 'admin'       : return '/admin';
      case 'superadmin'  : return '/analytics';
      default            : return '/login';
    }
  };

  const allow = roles => roles.includes(user?.role)
    ? undefined
    : <Navigate to="/login" replace />;

  return (
    <div className="app-container">
      {/* ========== HEADER ========== */}
      <header className="app-header">
        <div className="brand-bar" />

        <div className="header-inner">
          <div className="header-left">
            {/* Put your file in /public as /bnbwu_logo.png */}
            <img
              src="/bnbwu_logo.png"
              alt="The Begum Nusrat Bhutto Women University, Sukkur"
              className="app-logo"
            />

            <div className="title-wrap">
              <h1 className="app-title">
                {pathname.startsWith('/guest-house')
                  ? 'Guest-House Management System'
                  : 'BNBWU GATE-TRACKING SYSTEM'}
              </h1>

              <p className="current-user">
                {user ? (
                  <>
                    Signed in as <strong>{user.name}</strong>
                    <span className="role-chip">{user.role}</span>
                  </>
                ) : (
                  <>Not signed in</>
                )}
              </p>
            </div>
          </div>

          {user && (
            <button className="logout-button" onClick={handleLogout}>
              Logout
            </button>
          )}
        </div>
      </header>

      {/* ========== ROUTES ========== */}
      <main className="app-content">
        <Routes>
          <Route path="/" element={<Navigate to={defaultPath()} replace />} />

          {/* auth */}
          <Route path="/login"    element={<Login />} />
          <Route path="/signup"   element={<Signup />} />
          <Route path="/forgot"   element={<ForgotPassword />} />
          <Route path="/register" element={<Register />} />
          <Route path="/change-password-first" element={<ChangePasswordFirst />} />

          {/* student */}
          <Route path="/student"
                 element={ user?.role==='student' ? <StudentDashboard/> : <Navigate to="/login" replace /> } />

          {/* warden */}
          <Route path="/warden"
                 element={ user?.role==='warden' ? <WardenDashboard/> : <Navigate to="/login" replace /> } />

          {/* gate-keeper */}
          <Route path="/gatekeeper"
                 element={ user?.role==='gatekeeper' ? <GatekeeperDashboard/> : <Navigate to="/login" replace /> } />

          {/* admin */}
          <Route path="/admin"
                 element={
                   user?.role==='admin'
                     ? <AdminDashboard/>
                     : user?.role==='superadmin'
                       ? <Navigate to="/analytics" replace />
                       : <Navigate to="/login"    replace />
                 } />

          {/* analytics */}
          <Route path="/analytics"
                 element={ allow(['admin','superadmin']) ?? <AnalyticsDashboard/> } />
          <Route path="/analytics/students"
                 element={ allow(['admin','superadmin']) ?? <StudentsTable/> } />
          <Route path="/analytics/guests"
                 element={ allow(['admin','superadmin']) ?? <GuestsTable/> } />
          <Route path="/analytics/users"
                 element={ allow(['admin','superadmin','warden']) ?? <UsersTable/> } />
          <Route path="/analytics/hostels"
                 element={ allow(['admin','superadmin']) ?? <HostelsManagement/> } />
          <Route path="/analytics/warden-assignment"
                 element={ allow(['admin','superadmin']) ?? <WardenAssignment/> } />
          <Route path="/analytics/settings"
                 element={ allow(['superadmin']) ?? <SystemSettings/> } />

          {/* VC-office public view */}
          <Route path="/vc-office" element={<VcOfficeGuests/>} />

          {/* gate-keeper live guest entry */}
          <Route path="/gatekeeper/guests"
                 element={ user?.role==='gatekeeper' ? <GuestEntry/> : <Navigate to="/login" replace /> } />

          {/* 404 */}
          <Route path="*" element={<h2>404 — Page Not Found</h2>} />
        </Routes>
      </main>

      {/* ========== FOOTER ========== */}
      <footer className="app-footer">
        <div className="footer-inner">
          <span>
            Developed &amp; Deployed By{' '}
            <strong>
              <a
                href="https://aimtech.pk"
                target="_blank"
                rel="noopener noreferrer"
              >
                AIMs Technologies
              </a>
            </strong>
          </span>
          <span className="separator">•</span>
          <span>{year}</span>
          <span className="separator">•</span>
          <span>Version 2.0.3</span>
        </div>
      </footer>
    </div>
  );
}
